import { useState, useEffect, useRef } from "react";
import type { EditorDraft } from "./editor-types";
import {
  loadTemplate,
  getPalette,
  getFontPairing,
  type PublishedNavigationItem,
  type SectionData,
} from "@profolio/templates/browser";

export interface PreviewPageItem {
  id: string;
  slug: string;
  title: string;
  navigationLabel?: string | null;
  isHome: boolean;
  sortOrder: number;
  visible?: boolean;
}

export interface UsePreviewHtmlOptions {
  pages?: PreviewPageItem[];
  activeSlug?: string;
}

export function usePreviewHtml(
  draft: EditorDraft | null,
  options?: UsePreviewHtmlOptions
) {
  const [html, setHtml] = useState<string>("");
  const frameRef = useRef<number>();
  const generationToken = useRef<number>(0);

  const pages = options?.pages;
  const activeSlug = options?.activeSlug;

  useEffect(() => {
    if (!draft) return;

    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }

    const currentToken = ++generationToken.current;

    frameRef.current = requestAnimationFrame(async () => {
      try {
        const start = performance.now();
        const templateFn = await loadTemplate(draft.profile.themeKey || "classic-academic");

        // Discard if a newer generation was requested while this template loaded
        if (currentToken !== generationToken.current) return;

        const palette = getPalette(draft.profile.colorPalette || "ocean-blue");
        const fonts = getFontPairing(draft.profile.fontPairing || "serif-classic");

        let renderedSections: SectionData[] = draft.sections;
        let renderedProfile = draft.profile;

        if (pages && pages.length > 0) {
          const visiblePages = pages.filter((p) => p.visible !== false);
          const currentPage =
            visiblePages.find((p) =>
              activeSlug !== undefined && activeSlug !== null
                ? p.slug === activeSlug
                : p.isHome
            ) ||
            visiblePages.find((p) => p.isHome) ||
            visiblePages[0];

          if (currentPage) {
            renderedSections = (draft.sections || []).filter((s) => {
              if (s.pageId) return s.pageId === currentPage.id;
              return currentPage.isHome;
            });

            const navigation: PublishedNavigationItem[] = visiblePages.map((p) => ({
              id: p.id,
              slug: p.slug,
              label: p.navigationLabel || p.title,
              isHome: p.isHome,
              sortOrder: p.sortOrder,
            }));

            renderedProfile = {
              ...draft.profile,
              navigation,
              activeSlug: currentPage.slug,
              currentPageTitle: currentPage.title,
            };
          }
        }

        const newHtml = templateFn({
          profile: renderedProfile,
          sections: renderedSections,
          palette,
          fonts,
        });

        const end = performance.now();
        if (import.meta.env.DEV) {
          console.log(`[Preview Render] HTML generation took ${(end - start).toFixed(2)}ms`);
        }

        setHtml(newHtml);
      } catch (err) {
        console.error("Failed to render preview html", err);
      }
    });

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [draft, pages, activeSlug]);

  return html;
}
